import { Component, EventEmitter, Input, Output, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { SessionService } from '../../services/session.service';

export interface ProjectData {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string | null;
  sites: { domain: string }[];
}

export interface EditProjectData {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string | null;
  sites: { id: string; name: string; url?: string }[];
}

@Component({
  selector: 'app-create-project-panel',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './create-project-panel.component.html',
  styleUrls: ['./create-project-panel.component.scss']
})
export class CreateProjectPanelComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() editProject: EditProjectData | null = null;
  @Output() closePanel = new EventEmitter<void>();
  @Output() projectCreated = new EventEmitter<ProjectData>();
  @Output() projectUpdated = new EventEmitter<ProjectData>();

  public createProjectForm: FormGroup;
  public sitesExpanded = false;
  public isDraggingFile = false;
  public iconPreview: string | null = null;
  public isEditMode = false;

  public colorPalette = [
    { value: '#5F42D9', name: 'Violet' },
    { value: '#3B6FCE', name: 'Blue' },
    { value: '#56B84A', name: 'Green' },
    { value: '#F86807', name: 'Orange' },
    { value: '#A52CCD', name: 'Purple' },
    { value: '#F0B800', name: 'Yellow' },
    { value: '#C30000', name: 'Red' },
    { value: '#2F2F2F', name: 'Dark' }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly session: SessionService
  ) {
    this.createProjectForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      client: [''],
      description: [''],
      color: ['#5F42D9'],
      icon: [null],
      sites: this.fb.array([])
    });
  }

  get sites(): FormArray {
    return this.createProjectForm.get('sites') as FormArray;
  }

  get selectedColor(): string {
    return this.createProjectForm.get('color')?.value || '#5F42D9';
  }

  get projectInitials(): string {
    const name = this.createProjectForm.get('name')?.value || '';
    if (!name) return '';
    const words = name.trim().split(' ');
    if (words.length > 1) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  /** Show Save Changes / Create Project button: in edit mode only for org admins; in create mode always */
  get showSaveButton(): boolean {
    if (!this.isEditMode) return true;
    return this.session.isOrganizationAdmin();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editProject'] && this.editProject) {
      this.isEditMode = true;
      this.populateFormForEdit(this.editProject);
    } else if (changes['isOpen'] && !this.isOpen) {
      this.isEditMode = false;
    }
  }

  private populateFormForEdit(project: EditProjectData): void {
    // Reset form first
    this.resetForm();

    // Populate form with project data
    this.createProjectForm.patchValue({
      id: project.id,
      name: project.name,
      description: project.description || '',
      color: project.color,
      icon: project.icon || null
    });

    // Add existing sites
    if (project.sites && project.sites.length > 0) {
      this.sitesExpanded = true;
      project.sites.forEach(site => {
        const siteGroup = this.fb.group({
          domain: [site.url || site.name, [Validators.pattern(/^[a-zA-Z0-9][a-zA-Z0-9-]*(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/)]]
        });
        this.sites.push(siteGroup);
      });
    }

    // Set icon preview if exists
    if (project.icon) {
      this.iconPreview = project.icon;
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.onClose();
    }
  }

  public onClose(): void {
    this.closePanel.emit();
    this.resetForm();
    this.isEditMode = false;
  }

  public onOverlayClick(event: Event): void {
    if ((event.target as HTMLElement).classList.contains('panel-overlay')) {
      this.onClose();
    }
  }

  public selectColor(color: string): void {
    this.createProjectForm.patchValue({ color });
  }

  public toggleSitesSection(): void {
    this.sitesExpanded = !this.sitesExpanded;
    if (this.sitesExpanded && this.sites.length === 0) {
      this.addSite();
    }
  }

  public addSite(): void {
    const siteGroup = this.fb.group({
      domain: ['', [Validators.pattern(/^[a-zA-Z0-9][a-zA-Z0-9-]*(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/)]]
    });
    this.sites.push(siteGroup);
  }

  public removeSite(index: number): void {
    this.sites.removeAt(index);
  }

  // Icon upload handling
  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFile = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelect(files[0]);
    }
  }

  public onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelect(input.files[0]);
    }
  }

  private handleFileSelect(file: File): void {
    if (!file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.iconPreview = e.target?.result as string;
      this.createProjectForm.patchValue({ icon: this.iconPreview });
    };
    reader.readAsDataURL(file);
  }

  public removeIcon(): void {
    this.iconPreview = null;
    this.createProjectForm.patchValue({ icon: null });
  }

  public onSubmit(): void {
    if (this.createProjectForm.valid) {
      const formValue = this.createProjectForm.value;

      // Filter out empty sites
      const validSites = formValue.sites
        .filter((site: { domain: string }) => site.domain && site.domain.trim() !== '')
        .map((site: { domain: string }) => ({ domain: site.domain.trim() }));

      const projectData: ProjectData = {
        id: this.isEditMode && this.editProject ? this.editProject.id : '',
        name: formValue.name,
        description: formValue.description || '',
        color: formValue.color,
        icon: formValue.icon,
        sites: validSites
      };

      if (this.isEditMode) {
        this.projectUpdated.emit(projectData);
      } else {
        this.projectCreated.emit(projectData);
      }
      this.onClose();
    } else {
      this.createProjectForm.markAllAsTouched();
    }
  }

  private resetForm(): void {
    this.createProjectForm.reset({
      name: '',
      client: '',
      description: '',
      color: '#5F42D9',
      icon: null
    });
    this.sites.clear();
    this.sitesExpanded = false;
    this.iconPreview = null;
  }
}
